import {
  IInventoryRule,
  IInventoryRuleAdBreak,
} from '~/src/dash/apiV2/inventory-rules/inventoryRules';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import {
  convertSetIntoArrayOfStrings,
  convertSetIntoSingleNumber,
  convertSetIntoSingleString,
} from '~/src/dash/components/filters/conversions';
import { IExtendedIFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import {
  IListViewTableColumns,
  IListViewTablePagination,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { DateTimeRenderUnchanged } from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { generateOptionsListAsPromise } from '~/src/dash/components/dropdowns/common';
import { getInventoryRulesFilters } from '~/src/dash/apiV2/inventory-rules/inventoryRules';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import {
  convertResponseTypeToTKey,
  convertRuleTypeToTKey,
  convertTimezoneTypeToTKey,
  InventoryRuleNameWithDefault,
  RuleTypeOptions,
} from '~/src/dash/views/inventory-rules-v2/common';

export interface IInventoryRulesListConfig {
  cypressClassName: string;
  startToEndDateRanges: IStartToEndDateRanges;
  defaultState: {
    activeColumnKeys: string[];
    orderDirection: Order;
    orderBy: TInventoryRulesListColumnKey;
    page: number;
    rowsPerPage: RowsPerPage;
  };
  timezone: string;
  filters: Record<string, IExtendedIFilterRowObject>;
  columns: IListViewTableColumns;
  table: IListViewTablePagination;
  activeColumnKeys: string[];
  columnSelector: {
    resetKeys: string[];
  };
  drawer: {
    adBreaks: {
      defaultState: {
        activeColumnKeys: string[];
        orderDirection: Order;
        orderBy: string;
        page: number;
        rowsPerPage: RowsPerPage;
      };
      columns: IListViewTableColumns;
    };
  };
}

export const inventoryRulesListConfig: IInventoryRulesListConfig = {
  cypressClassName: 'inventory-rules-list',
  startToEndDateRanges: {
    startDateFrom: null,
    startDateTo: null,
    endDateFrom: null,
    endDateTo: null,
  },
  defaultState: {
    activeColumnKeys: [
      'RuleType',
      'LookupKey',
      'LookupValue',
      'StartDateTime',
      'EndDateTime',
      'TimezoneType',
      'ContentTitle',
      'ContentGenre',
      'ContentCategory',
      'ContentNetwork',
    ],
    orderDirection: 'desc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 25,
  },
  timezone: 'UTC',
  filters: {
    LookupKey: {
      key: 'LookupKey',
      tKey: 'Key',
      getList: (session: ISessionState) =>
        getInventoryRulesFilters(session.publisher.ID).then((data) => data.LookupKey || []),
      selectedLimit: 1,
      queryKey: 'LookupKey',
      formatQueryValue: convertSetIntoSingleString,
    },
    RuleType: {
      key: 'RuleType',
      tKey: 'FormatType',
      getList: () => generateOptionsListAsPromise(RuleTypeOptions),
      selectedLimit: 1,
      disableSearch: true,
      disableSort: true,
      queryKey: 'RuleType',
      formatQueryValue: convertSetIntoSingleNumber,
    },
    ContentCategory: {
      key: 'ContentCategory',
      tKey: 'ContentCategory',
      getList: (session: ISessionState) =>
        getInventoryRulesFilters(session.publisher.ID).then((data) => data.ContentCategory || []),
      selectedLimit: 0,
      queryKey: 'ContentCategory',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentGenre: {
      key: 'ContentGenre',
      tKey: 'ContentGenre',
      getList: (session: ISessionState) =>
        getInventoryRulesFilters(session.publisher.ID).then((data) => data.ContentGenre || []),
      selectedLimit: 0,
      queryKey: 'ContentGenre',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentNetwork: {
      key: 'ContentNetwork',
      tKey: 'ContentNetwork',
      getList: (session: ISessionState) =>
        getInventoryRulesFilters(session.publisher.ID).then((data) => data.ContentNetwork || []),
      selectedLimit: 0,
      queryKey: 'ContentNetwork',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    ContentTitle: {
      key: 'ContentTitle',
      tKey: 'ContentTitle',
      getList: (session: ISessionState) =>
        getInventoryRulesFilters(session.publisher.ID).then((data) => data.ContentTitle || []),
      selectedLimit: 0,
      queryKey: 'ContentTitle',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
  },
  table: {
    order: 'desc',
    orderBy: 'ID',
    page: 0,
    rowsPerPage: 25,
  },
  activeColumnKeys: [
    'RuleType',
    'LookupKey',
    'LookupValue',
    'StartDateTime',
    'EndDateTime',
    'TimezoneType',
    'ContentTitle',
    'ContentGenre',
    'ContentCategory',
    'ContentNetwork',
  ],
  columnSelector: {
    resetKeys: [
      'RuleType',
      'LookupKey',
      'LookupValue',
      'StartDateTime',
      'EndDateTime',
      'TimezoneType',
      'ContentTitle',
      'ContentGenre',
      'ContentCategory',
      'ContentNetwork',
    ],
  },
  columns: {
    ID: {
      formatValue: (value: number) => value.toString(),
      isHidden: true,
      isSearchable: false,
      key: 'ID',
      label: 'ID',
      requestKey: 'ID',
      responseKey: 'ID',
      valueType: 'string',
    },
    Name: {
      cellJSX: InventoryRuleNameWithDefault,
      cellRenderType: 'name', // TODO: change to 'name_link' once getItemSettingsRoute is implemented
      defaultOrderDirection: 'asc',
      headerRenderType: 'name',
      isSearchable: true,
      key: 'Name',
      label: 'Name',
      tKey: 'Name',
      requestKey: 'Name',
      responseKey: 'Name',
      valueType: 'string',
      orderByKey: 'Name',
    },
    RuleType: {
      disableSort: true,
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'RuleType',
      tKey: 'FormatType',
      label: 'Format Type',
      requestKey: '',
      responseKey: 'RuleType',
      valueType: 'string',
      formatValue: (value: number) => convertRuleTypeToTKey(value),
    },
    ResponseType: {
      disableSort: true,
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ResponseType',
      tKey: 'ResponseType',
      label: 'Response Type',
      requestKey: '',
      responseKey: 'ResponseType',
      valueType: 'string',
      formatValue: (value: number) => convertResponseTypeToTKey(value),
    },
    LookupKey: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'LookupKey',
      tKey: 'Key',
      label: 'Key',
      requestKey: '',
      responseKey: 'LookupKey',
      valueType: 'string',
    },
    LookupValue: {
      disableSort: true,
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'LookupValue',
      tKey: 'Value',
      label: 'Value',
      requestKey: '',
      responseKey: 'LookupValue',
      valueType: 'string',
      orderByKey: 'Value',
    },
    StartDateTime: {
      cellJSX: DateTimeRenderUnchanged,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'StartDateTime',
      label: 'Start Time',
      tKey: 'StartTime',
      orderByKey: 'StartDateTime',
      requestKey: '',
      responseKey: 'StartDateTime',
      valueType: 'date',
    },
    EndDateTime: {
      cellJSX: DateTimeRenderUnchanged,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'EndDateTime',
      label: 'End Time',
      tKey: 'EndTime',
      orderByKey: 'EndDateTime',
      requestKey: '',
      responseKey: 'EndDateTime',
      valueType: 'date',
    },
    TimezoneType: {
      disableSort: true,
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'TimezoneType',
      tKey: 'TimezoneType',
      label: 'Timezone Type',
      requestKey: '',
      responseKey: 'TimezoneType',
      valueType: 'string',
      formatValue: (value: number) => convertTimezoneTypeToTKey(value),
    },
    ContentCategory: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentCategory',
      tKey: 'ContentCategory',
      label: 'Content Category',
      requestKey: '',
      responseKey: 'ContentCategory',
      valueType: 'string',
      orderByKey: 'ContentCategory',
    },
    ContentChannel: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentChannel',
      tKey: 'ContentChannel',
      label: 'Content Channel',
      requestKey: '',
      responseKey: 'ContentChannel',
      valueType: 'string',
      orderByKey: 'ContentChannel',
    },
    ContentContext: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentContext',
      tKey: 'ContentContext',
      label: 'Content Context',
      requestKey: '',
      responseKey: 'ContentContext',
      valueType: 'string',
      orderByKey: 'ContentContext',
    },
    ContentDistributionID: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentDistributionID',
      tKey: 'ContentDistributionID',
      label: 'Content Distribution ID',
      requestKey: '',
      responseKey: 'ContentDistributionID',
      valueType: 'string',
      orderByKey: 'ContentDistributionID',
    },
    ContentDistributionName: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentDistributionName',
      tKey: 'ContentDistributionName',
      label: 'Content Distribution Name',
      requestKey: '',
      responseKey: 'ContentDistributionName',
      valueType: 'string',
      orderByKey: 'ContentDistributionName',
    },
    ContentEpisode: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentEpisode',
      tKey: 'ContentEpisode',
      label: 'Content Episode',
      requestKey: '',
      responseKey: 'ContentEpisode',
      valueType: 'string',
      orderByKey: 'ContentEpisode',
    },
    ContentGenre: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentGenre',
      tKey: 'ContentGenre',
      label: 'Content Genre',
      requestKey: '',
      responseKey: 'ContentGenre',
      valueType: 'string',
      orderByKey: 'ContentGenre',
    },
    ContentID: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentID',
      tKey: 'ContentID',
      label: 'Content ID',
      requestKey: '',
      responseKey: 'ContentID',
      valueType: 'string',
      orderByKey: 'ContentID',
    },
    ContentKeywords: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentKeywords',
      tKey: 'ContentKeywords',
      label: 'Content Keywords',
      requestKey: '',
      responseKey: 'ContentKeywords',
      valueType: 'string',
      orderByKey: 'ContentKeywords',
    },
    ContentLanguage: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentLanguage',
      tKey: 'ContentLanguage',
      label: 'Content Language',
      requestKey: '',
      responseKey: 'ContentLanguage',
      valueType: 'string',
      orderByKey: 'ContentLanguage',
    },
    ContentLength: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentLength',
      tKey: 'ContentLength',
      label: 'Content Length',
      requestKey: '',
      responseKey: 'ContentLength',
      valueType: 'string',
      orderByKey: 'ContentLength',
    },
    ContentNetwork: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentNetwork',
      tKey: 'ContentNetwork',
      label: 'Content Network',
      requestKey: '',
      responseKey: 'ContentNetwork',
      valueType: 'string',
      orderByKey: 'ContentNetwork',
    },
    ContentProdQuality: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentProdQuality',
      tKey: 'ContentProdQuality',
      label: 'Content Prod Quality',
      requestKey: '',
      responseKey: 'ContentProdQuality',
      valueType: 'string',
      orderByKey: 'ContentProdQuality',
    },
    ContentProducerID: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentProducerID',
      tKey: 'ContentProducerID',
      label: 'Content Producer ID',
      requestKey: '',
      responseKey: 'ContentProducerID',
      valueType: 'string',
      orderByKey: 'ContentProducerID',
    },
    ContentProducer: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentProducer',
      tKey: 'ContentProducer',
      label: 'Content Producer',
      requestKey: '',
      responseKey: 'ContentProducer',
      valueType: 'string',
      orderByKey: 'ContentProducer',
    },
    ContentRating: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentRating',
      tKey: 'ContentRating',
      label: 'Content Rating',
      requestKey: '',
      responseKey: 'ContentRating',
      valueType: 'string',
      orderByKey: 'ContentRating',
    },
    ContentSeason: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentSeason',
      tKey: 'ContentSeason',
      label: 'Content Season',
      requestKey: '',
      responseKey: 'ContentSeason',
      valueType: 'string',
      orderByKey: 'ContentSeason',
    },
    ContentSeries: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentSeries',
      tKey: 'ContentSeries',
      label: 'Content Series',
      requestKey: '',
      responseKey: 'ContentSeries',
      valueType: 'string',
      orderByKey: 'ContentSeries',
    },
    ContentTaxonomy: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentTaxonomy',
      tKey: 'ContentTaxonomy',
      label: 'Content Taxonomy',
      requestKey: '',
      responseKey: 'ContentTaxonomy',
      valueType: 'string',
      orderByKey: 'ContentTaxonomy',
    },
    ContentTitle: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentTitle',
      tKey: 'ContentTitle',
      label: 'Content Title',
      requestKey: '',
      responseKey: 'ContentTitle',
      valueType: 'string',
      orderByKey: 'ContentTitle',
    },
    ContentURL: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'ContentURL',
      tKey: 'ContentURL',
      label: 'Content URL',
      requestKey: '',
      responseKey: 'ContentURL',
      valueType: 'string',
      orderByKey: 'ContentURL',
    },
    Custom5: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom5',
      tKey: 'Custom5',
      label: 'Custom 5',
      requestKey: '',
      responseKey: 'Custom5',
      valueType: 'string',
      orderByKey: 'Custom5',
    },
    Custom6: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom6',
      tKey: 'Custom6',
      label: 'Custom 6',
      requestKey: '',
      responseKey: 'Custom6',
      valueType: 'string',
      orderByKey: 'Custom6',
    },
    Custom7: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom7',
      tKey: 'Custom7',
      label: 'Custom 7',
      requestKey: '',
      responseKey: 'Custom7',
      valueType: 'string',
      orderByKey: 'Custom7',
    },
    Custom8: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom8',
      tKey: 'Custom8',
      label: 'Custom 8',
      requestKey: '',
      responseKey: 'Custom8',
      valueType: 'string',
      orderByKey: 'Custom8',
    },
    Custom9: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom9',
      tKey: 'Custom9',
      label: 'Custom 9',
      requestKey: '',
      responseKey: 'Custom9',
      valueType: 'string',
      orderByKey: 'Custom9',
    },
    Custom10: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom10',
      tKey: 'Custom10',
      label: 'Custom 10',
      requestKey: '',
      responseKey: 'Custom10',
      valueType: 'string',
      orderByKey: 'Custom10',
    },
    Custom11: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom11',
      tKey: 'Custom11',
      label: 'Custom 11',
      requestKey: '',
      responseKey: 'Custom11',
      valueType: 'string',
      orderByKey: 'Custom11',
    },
    Custom12: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom12',
      tKey: 'Custom12',
      label: 'Custom 12',
      requestKey: '',
      responseKey: 'Custom12',
      valueType: 'string',
      orderByKey: 'Custom12',
    },
    Custom13: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom13',
      tKey: 'Custom13',
      label: 'Custom 13',
      requestKey: '',
      responseKey: 'Custom13',
      valueType: 'string',
      orderByKey: 'Custom13',
    },
    Custom14: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom14',
      tKey: 'Custom14',
      label: 'Custom 14',
      requestKey: '',
      responseKey: 'Custom14',
      valueType: 'string',
      orderByKey: 'Custom14',
    },
    Custom15: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom15',
      tKey: 'Custom15',
      label: 'Custom 15',
      requestKey: '',
      responseKey: 'Custom15',
      valueType: 'string',
      orderByKey: 'Custom15',
    },
    Custom16: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom16',
      tKey: 'Custom16',
      label: 'Custom 16',
      requestKey: '',
      responseKey: 'Custom16',
      valueType: 'string',
      orderByKey: 'Custom16',
    },
    Custom17: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom17',
      tKey: 'Custom17',
      label: 'Custom 17',
      requestKey: '',
      responseKey: 'Custom17',
      valueType: 'string',
      orderByKey: 'Custom17',
    },
    Custom18: {
      headerRenderType: 'string',
      cellRenderType: 'string',
      key: 'Custom18',
      tKey: 'Custom18',
      label: 'Custom 18',
      requestKey: '',
      responseKey: 'Custom18',
      valueType: 'string',
      orderByKey: 'Custom18',
    },
  },
  drawer: {
    adBreaks: {
      defaultState: {
        activeColumnKeys: [
          'PodDuration',
          'SlotCount',
          'MinAdDuration',
          'MaxAdDuration',
          'CuePointValue',
          'CuePointType',
        ],
      },
      columns: {
        ID: {
          formatValue: (value: number) => String(value),
          isHidden: true,
          key: 'ID',
          label: 'ID',
          requestKey: '',
          responseKey: 'ID',
          valueType: 'string',
        },
        Name: {
          defaultOrderDirection: 'asc',
          headerRenderType: 'name_nonsorted',
          cellRenderType: 'name_nonsorted',
          key: 'Name',
          tKey: 'AdBreakID',
          label: 'Ad Break ID',
          requestKey: 'AdBreakID',
          responseKey: 'AdBreakID',
          valueType: 'string',
          orderByKey: 'Name',
          formatValue: (value: number) => String(value),
          disableSort: true,
        },
        PodDuration: {
          defaultOrderDirection: 'asc',
          headerRenderType: 'number_small',
          cellRenderType: 'number',
          key: 'PodDuration',
          tKey: 'PodDurations',
          label: 'Pod Duration',
          requestKey: '',
          responseKey: 'PodDuration',
          valueType: 'number',
          orderByKey: 'PodDuration',
          disableSort: true,
        },
        SlotCount: {
          defaultOrderDirection: 'asc',
          headerRenderType: 'number_small',
          cellRenderType: 'number',
          key: 'SlotCount',
          tKey: 'SlotCount',
          label: 'Slot Count',
          requestKey: '',
          responseKey: 'SlotCount',
          valueType: 'number',
          orderByKey: 'SlotCount',
          disableSort: true,
        },
        MinAdDuration: {
          defaultOrderDirection: 'asc',
          headerRenderType: 'number_small',
          cellRenderType: 'number',
          key: 'MinAdDuration',
          tKey: 'MinAdDuration',
          label: 'Min Ad Dur.',
          requestKey: '',
          responseKey: 'MinAdDuration',
          valueType: 'number',
          orderByKey: 'MinAdDuration',
          disableSort: true,
        },
        MaxAdDuration: {
          defaultOrderDirection: 'asc',
          headerRenderType: 'number_small',
          cellRenderType: 'number',
          key: 'MaxAdDuration',
          tKey: 'MaxAdDuration',
          label: 'Max Ad Dur.',
          requestKey: '',
          responseKey: 'MaxAdDuration',
          valueType: 'number',
          orderByKey: 'MaxAdDuration',
          disableSort: true,
        },
        CuePointType: {
          defaultOrderDirection: 'asc',
          headerRenderType: 'number_small',
          cellRenderType: 'number',
          key: 'CuePointType',
          tKey: 'CuePointType',
          label: 'Cue Point Type',
          requestKey: '',
          responseKey: 'CuePointType',
          valueType: 'number',
          orderByKey: 'CuePointType',
          disableSort: true,
        },
        CuePointValue: {
          defaultOrderDirection: 'asc',
          headerRenderType: 'number_small',
          cellRenderType: 'number',
          key: 'CuePointValue',
          tKey: 'CuePointValue',
          label: 'Cue Point Value',
          requestKey: '',
          responseKey: 'CuePointValue',
          valueType: 'string',
          orderByKey: 'CuePointValue',
          disableSort: true,
        },
      },
    },
  },
} as unknown as IInventoryRulesListConfig;

export type TInventoryRulesListColumnKey = keyof typeof inventoryRulesListConfig.columns;

export const formatInventoryRulesResponseIntoLVTRows = (
  rules: IInventoryRule[],
  cfg: IListViewTableColumns,
): IListViewTableRow[] => {
  return rules.map((rule) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      let value: unknown = rule[responseKey as keyof IInventoryRule];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    return formattedRow;
  });
};

export const formatAdBreaksDataIntoLVTRows = (
  data: IInventoryRuleAdBreak[],
  cfg: IInventoryRulesListConfig['drawer']['adBreaks']['columns'],
): IListViewTableRow[] => {
  return data.map((datum) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      let value: unknown = datum[responseKey as keyof IInventoryRuleAdBreak];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    return formattedRow;
  });
};
